import { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Bell, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const OverrunSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ enabled: false, threshold_percent: 25, email: "" });

  useEffect(() => {
    api.getOverrunSettings()
      .then((r) => setForm({
        enabled: !!r.data.enabled,
        threshold_percent: r.data.threshold_percent ?? 25,
        email: r.data.email || "",
      }))
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.updateOverrunSettings({
        enabled: form.enabled,
        threshold_percent: parseFloat(form.threshold_percent) || 25,
        email: form.email?.trim() || "",
      });
      setForm({
        enabled: !!data.enabled,
        threshold_percent: data.threshold_percent,
        email: data.email || "",
      });
      toast.success("Réglages enregistrés");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="overrun-settings-tab">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-600" />
            Alertes de dépassement horaire
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Recevez un email automatique dès qu'un chantier dépasse le seuil horaire configuré (comparaison temps réel vs théorique).
            Le scheduler est déclenché chaque jour à 8h45 (Europe/Paris). Limite : 1 email / commande / 7 jours.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="overrun-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: !!v })}
              data-testid="overrun-enabled"
            />
            <Label htmlFor="overrun-enabled" className="cursor-pointer">
              Activer les alertes
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="overrun-threshold">Seuil de dépassement (%)</Label>
              <Input
                id="overrun-threshold"
                type="number"
                min={1}
                max={500}
                step="1"
                value={form.threshold_percent}
                onChange={(e) => setForm({ ...form, threshold_percent: e.target.value })}
                data-testid="overrun-threshold"
              />
              <p className="text-xs text-muted-foreground">
                Alerte envoyée quand le temps réel dépasse le théorique de ≥ {form.threshold_percent}%.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="overrun-email">Email destinataire</Label>
              <Input
                id="overrun-email"
                type="email"
                placeholder="Laisser vide pour l'email du compte admin"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="overrun-email"
              />
            </div>
          </div>

          {!form.enabled && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Les alertes sont désactivées — aucun email ne sera envoyé.</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="overrun-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Enregistrer</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverrunSettingsTab;
